<script setup lang="ts">
import { computed } from 'vue'
import UIKit, { type ITableColumn } from '@pocketprep/ui-kit'
import type { TGroupQuestionRow } from '@/store/types'

const { Icon } = UIKit

const props = withDefaults(
    defineProps<{
        row: TGroupQuestionRow
        column: ITableColumn
        numOfQuestions?: number
    }>(),
    { numOfQuestions: 0 }
)

const questionPrompt = computed(() => {
    return props.row.scenarioPartLabel
        ? `${props.row.scenarioPartLabel}: ${props.row.prompt}`
        : props.row.prompt
})
</script>

<template>
    <div
        class="group-question-row"
    >
        <template v-if="props.column.propName === 'question'">
            <div v-if="props.column.propName === 'question'" class="group-question-row__subject">
                {{ props.row.subject }}
            </div>
        </template>
        <template v-if="props.column.propName === 'question'">
            <div
                class="group-question-row__prompt"
                :class="{'group-question-row__prompt--all': props.numOfQuestions > 3 }"
                v-html="questionPrompt"
            />
        </template>
        <template v-if="props.column.propName === 'numStudentsAnswered'">
            <div class="group-question-row__answered">
                <div class="group-question-row__number-of-students-answered">
                    {{ props.row.numStudentsAnswered }}
                </div>
                <Icon
                    class="group-question-row__person-icon"
                    type="person"
                />
            </div>
        </template>
        <template v-if="props.column.propName === 'percentStudentsCorrect'">
            <div class="group-question-row__percent">
                <div class="group-question-row__percent-students-correct">
                    {{ props.row.percentStudentsCorrect }}
                </div>
                <Icon
                    class="group-question-row__percent-icon"
                    type="percent"
                />
            </div>
        </template>
        <template v-if="props.column.propName === 'numStudentsFlagged'">
            <div class="group-question-row__flagged">
                <div class="group-question-row__number-of-students-flagged">
                    {{ props.row.numStudentsFlagged }}
                </div>
                <Icon
                    class="group-question-row__flag-icon"
                    type="flagContent"
                />
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>


.group-question-row {
    &__subject {
        margin-bottom: 6px;
        color: $slate;
        font-size: 14px;
        line-height: 18px;
        width: 254px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
    }

    &__prompt {
        color: $brand-black;
        white-space: normal;
        font-size: 15px;
        line-height: 22px;

        :deep(span) {
            position: relative;
            z-index: 1;

            &::before {
                border-radius: 4px;
                content: '';
                height: 20px;
                width: calc(100% + 2px);
                left: -1px;
                top: 0;
                background: $buttermilk;
                z-index: -1;
                position: absolute;
            }
        }

        &--all {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
        }
    }

    &__flaggged,
    &__answered,
    &__percent {
        height: 20px;
    }

    &__number-of-students-flagged,
    &__percent-students-correct,
    &__number-of-students-answered {
        align-items: center;
        justify-content: center;
        color: $brand-black;
        font-weight: 600;
        font-size: 15px;
        line-height: 20px;
        float: left;
    }

    &__number-of-students-answered {
        margin: 1.5px 4px 0 0;
    }

    &__percent-students-correct {
        margin: 1.5px 3px 0 0;
    }

    &__number-of-students-flagged {
        margin: 1.5px 1.5px 0 0;
    }

    &__flag-icon {
        margin: 4px 0 0 3.5px;
    }

    &__flagged {
        height: 20px;
        margin-right: 5px;
    }

    &__percent-icon {
        margin-top: 2.75px;
    }

    &__person-icon {
        margin-top: 1px;
    }

    &__flag-icon,
    &__person-icon {
        color: $slate;
    }
}
</style>
